import { useEffect, useRef, useState } from 'react';
import Lottie from 'lottie-react';
import individualIcon from '../../images/individual-lottie.json';
import entityIcon from '../../images/building-lottie.json';
import dateIcon from '../../images/birthday.svg';
import nationalityIcon from '../../images/passport.svg';
import countryIcon from '../../images/earth-location.svg';
import addressIcon from '../../images/address.svg';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { intakeRequest, prepareIntakeFiles, categoryLabels } from '../../utils/clientDocumentIntake';
import '../ui/css/ConfirmDialog.css';
import './AddClientModal.css';
const countryNames = new Intl.DisplayNames(['en'], {type:'region'});
const country = value => { try { return value ? countryNames.of(value.toUpperCase()) : ''; } catch { return value || ''; } };
const date = value => value ? new Date(`${value}T00:00:00`).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) : 'Not available';

export default function ClientDropIntakeRouter({ files, onAdd, onEdit, onClose }) {
  const callbacks = useRef({onAdd,onEdit}); callbacks.current={onAdd,onEdit};
  const cache=useRef(null);
  const [attempt,setAttempt]=useState(0);
  const [state,setState]=useState({loading:true,message:'Reading document...'});
  useEffect(()=>{
    const abort=new AbortController(); let alive=true;
    (async()=>{
      try {
        setState({loading:true,message:cache.current?'Checking existing clients...':'Reading document...'});
        let intake=cache.current;
        if(!intake) {
          const prepared=await prepareIntakeFiles(files);
          if(!alive)return;
          const result=await intakeRequest('extract',{files:prepared,context:{}},abort.signal,progress=>{
            if(alive)setState({loading:true,message:progress.stage==='extracting' && categoryLabels[progress.category]
              ? `Extracting data from ${progress.category === 'id_document' ? 'ID' : categoryLabels[progress.category]}...`:'Reading document...'});
          });
          if(!alive)return;
          intake={prepared,result};cache.current=intake;
        }
        const subjects=intake.result.documents.filter(d=>d.status==='extracted' && d.extraction?.fields?.clientName?.value?.trim()).map(d=>{
          const f=d.extraction.fields;
          return {name:f.clientName.value,clientType:f.clientType?.value || '',dateOfBirth:f.dateOfBirth?.value || '',incorporationDate:f.incorporationDate?.value || '',nationality:f.nationality?.value || '',country:f.country?.value || ''};
        });
        if(!subjects.length) {callbacks.current.onAdd(intake);return;}
        setState({loading:true,message:'Checking existing clients...'});
        const timeZone=Intl.DateTimeFormat().resolvedOptions().timeZone;
        const result=await intakeRequest('matches',{subjects,timeZone},abort.signal);
        if(!alive)return;
        if(!Array.isArray(result.matches)) throw new Error('Invalid matching response.');
        if(!result.matches.length) callbacks.current.onAdd(intake);
        else setState({matches:result.matches,hasMore:result.hasMore});
      } catch(error) {
        if(alive && error.name!=='AbortError')setState({error: cache.current
          ? 'The existing-client check could not be completed. Retry without reading the document again, or continue with a new client.'
          : error.message || 'The document could not be read.'});
      }
    })();
    return ()=>{alive=false;abort.abort();};
  },[files,attempt]);
  const title=state.matches ? 'Does this document belong to an existing client?' : state.error?'Unable to check documents':'Processing document';
  return <Dialog visible onHide={onClose} modal dismissableMask blockScroll showHeader={false} draggable={false} resizable={false}
    baseZIndex={10000} className="clientMatchDialog" maskClassName="clientIntakeUnidentifiedMask" aria-label={title}>
    <div className="appConfirmCard appConfirm--neutral clientMatchCard">
      <button type="button" className="clientMatchClose" onClick={onClose} aria-label="Close client matching"><i className="bi bi-x-lg" /></button>
      {state.loading ? <div role="status" aria-live="polite" className="clientMatchLoading">
        <div className="appDotsLoader" aria-hidden="true" /><p>{state.message}</p>
      </div> : <>
        <h2 className="appConfirmTitle">{title}</h2>
        <div className="clientMatchBody">
        {state.error ? <>
          <p className="appConfirmText" role="alert">{state.error}</p>
          <div className="appConfirmActions">
            <Button label="Retry" className="appConfirmBtn appConfirmBtnPrimary" onClick={()=>setAttempt(v=>v+1)} />
            {cache.current && <Button label="Add a new client" className="appConfirmBtn appConfirmBtnSecondary" onClick={()=>callbacks.current.onAdd(cache.current)} />}
          </div>
        </> : <>
          {state.hasMore && <p className="appConfirmText">Showing the 10 closest matches.</p>}
          <div className="clientMatchList">
            {state.matches.map(match=>{
              const isEntity = match.client.clientType === 'entity';
              const selectClient = () => callbacks.current.onEdit(match.client,cache.current);
              return <div className="clientMatchCandidate" key={match.client.id} onClick={selectClient}>
                <div className="clientMatchCandidateHeader">
                <div className="clientMatchIdentity">
                  <span className="clientMatchTypeIcon" aria-hidden="true"><Lottie animationData={isEntity ? entityIcon : individualIcon} loop={false} /></span>
                  <div><h3>{match.client.clientName}</h3><span className="clientMatchTypeLabel">{isEntity ? 'Entity' : 'Individual'}</span></div>
                </div>
                {!!match.differences.length && <div className="clientMatchDifferences">{match.differences.map(d=><span key={d}>{d}</span>)}</div>}
                </div>
                <dl>
                  <div className="clientMatchDetail"><img src={dateIcon} alt="" /><div><dt>{isEntity ? 'Incorporation date' : 'Date of birth'}</dt><dd>{date(match.details.date)}</dd></div></div>
                  <div className="clientMatchDetail"><img src={isEntity ? countryIcon : nationalityIcon} alt="" /><div><dt>{isEntity ? 'Country of incorporation' : 'Nationality'}</dt><dd>{country(match.details.nationality || (isEntity ? match.details.country : '')) || 'Not available'}</dd></div></div>
                  <div className="clientMatchDetail clientMatchAddress"><img src={addressIcon} alt="" /><div><dt>{isEntity ? 'Registered address' : 'Domicile'}</dt><dd>{[match.details.address,match.details.city,country(match.details.country)].filter(Boolean).join(', ') || 'Not available'}</dd></div></div>
                </dl>
                <div className="clientMatchCandidateFooter">
                  <Button label="→ Use this client" className="appConfirmBtn appConfirmBtnPrimary" onClick={event=>{event.stopPropagation();selectClient();}} />
                </div>
              </div>;
            })}
          </div>
          <Button label="NO → Add new client" className="appConfirmBtn appConfirmBtnSecondary clientMatchAddNew" onClick={()=>callbacks.current.onAdd(cache.current)} />
        </>}
        </div>
      </>}
    </div>
  </Dialog>;
}
